'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { FaApple } from 'react-icons/fa'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import AppStoreLink from '@/app/components/AppStoreLink'
import WaitlistModal from '@/app/components/WaitlistModal'

export default function PostContent() {
  // 2026-06-29: dropped the failing 7-day email-sequence signup. GA4
  // data over 28 days: 9 android_waitlist_open events but ~0 detox
  // email submits. Two reasons:
  //   1. Article gives the user every swap already, the 7-email
  //      sequence is more of the same with no marginal value.
  //   2. The Android-waitlist pattern works because it's a low-
  //      commitment hand-raise ("ping me when ready") that matches
  //      a discrete user desire. 7-email sequences are high-commitment.
  // Replacement: dual CTA, iOS users get the App Store, non-iOS
  // users get the Android waitlist via the same WaitlistModal that's
  // proving to convert on the homepage hero.
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false)

  return (
    <BlogPostLayout
      title="The 7-Day Microplastic Detox Challenge: What to Change First"
      author="MicroPlastics Editorial Team"
      date="May 25, 2026"
      readTime="13 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="7-day microplastic detox challenge, what to change first"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <p className="text-lg text-gray-700 leading-relaxed">
        You can&apos;t eliminate microplastics from modern life in seven
        days. But you can cut the obvious daily exposures: bottled
        water, hot drinks in plastic-lined cups, microwaved plastic,
        worn cutting boards, takeout containers, and tea bags made of
        nylon. One change per day, seven days, no overhaul required.
        By Sunday you&apos;ll have replaced the habits that account
        for most of an average household&apos;s avoidable exposure.
      </p>

      <BlogTLDR>
        <p className="mb-3">
          <strong>Quick answer:</strong> Seven days, seven swaps. Day 1:
          filter your tap water. Day 2: stop microwaving plastic. Day 3:
          replace your coffee cup routine. Day 4: audit food storage.
          Day 5: replace worn cutting boards. Day 6: check tea bags and
          hot drinks. Day 7: scan your pantry to set a baseline.
        </p>
        <p className="mb-3">
          <strong>Highest-risk situations addressed:</strong> bottled
          water exposure (~240,000 particles/L), microwaved plastic
          (4.22 million particles per cm² in 3 minutes), coffee cup
          shedding (~1,500 particles/cup), and cutting board release
          (up to 50g/year for polyethylene boards).
        </p>
        <p>
          <strong>Best first swap if you only do one day:</strong> Day 1, filter your tap water and refill into glass or stainless.
          This alone reduces more daily microplastic intake than any
          other single change.
        </p>
      </BlogTLDR>

      <BlogComparisonTable
        caption="The 7-day plan at a glance"
        columns={[
          { key: 'day', label: 'Day' },
          { key: 'swap', label: 'Swap' },
          { key: 'cost', label: 'Cost' },
          { key: 'time', label: 'Time' },
        ]}
        rows={[
          { day: 'Day 1', swap: 'Filter your tap water; refill into glass/stainless', cost: '$30-90', time: '15 min' },
          { day: 'Day 2', swap: 'Ban plastic from the microwave; use glass/ceramic', cost: '$0-40', time: '5 min' },
          { day: 'Day 3', swap: 'Replace daily takeaway coffee cup with a personal travel mug', cost: '$25-40', time: '5 min' },
          { day: 'Day 4', swap: 'Audit food storage, toss scratched plastic; switch to glass', cost: '$0-60', time: '30 min' },
          { day: 'Day 5', swap: 'Replace plastic cutting board (produce) with wood/bamboo', cost: '$20-60', time: '5 min' },
          { day: 'Day 6', swap: 'Switch to loose-leaf tea or paper-only certified tea bags', cost: '$10-20', time: '10 min' },
          { day: 'Day 7', swap: 'Scan your pantry with the app to set a baseline', cost: 'Free', time: '20 min' },
        ]}
      />

      <BlogKeyTakeaways
        items={[
          <>You can hit most of the daily exposure reduction in seven days with seven swaps.</>,
          <>Day 1 (water filter) accounts for the largest single chunk of typical household reduction.</>,
          <>Day 2 (never microwave plastic) is free and one of the highest-impact behavioral changes.</>,
          <>Total cost for the full 7-day plan: roughly $100-300, depending on which swaps you already have.</>,
          <>The behavioral changes (Day 2, Day 6, Day 7) cost nothing and stack with the product swaps.</>,
          <>By Day 7, you have a baseline you can track. Reduction is meaningful only if you measure it.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Day 1: Filter your tap water
      </h2>
      <p>
        <strong>Why it&apos;s first.</strong> Bottled water averages
        ~240,000 microplastic particles per liter (Qian 2024, PNAS).
        Unfiltered tap is typically a few to a few hundred particles
        per liter. A NSF P473-certified carbon block pitcher reduces
        microplastics by 80-99% AND eliminates the bottled-water habit
        that drives most of the day&apos;s exposure.
      </p>
      <p>
        <strong>What to do today:</strong>
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Buy or order a Clearly Filtered pitcher ($90), Epic Pure ($70), or AquaTru countertop RO ($450).</li>
        <li>Refill into a stainless or glass water bottle. Klean Kanteen and Hydro Flask are reliable, $20-40.</li>
        <li>Stop restocking single-use plastic water cases.</li>
      </ul>
      <p>
        See:{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          water filters compared
        </Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-in-bottled-water" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          bottled water microplastics
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Day 2: Stop microwaving plastic, ever
      </h2>
      <p>
        <strong>Why it matters.</strong> A 2023 University of Nebraska
        study found microwaving plastic releases up to{' '}
        <strong>4.22 million microplastic particles per cm²</strong> of
        container in 3 minutes. &ldquo;Microwave-safe&rdquo; just means
        the container won&apos;t melt, it does not mean particle-free.
      </p>
      <p>
        <strong>What to do today:</strong>
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Pull out a ceramic dinner plate. From now on: transfer takeout, frozen meals, and leftovers to that plate (or a glass bowl) before microwaving.</li>
        <li>If you don&apos;t have a glass food-storage set, this is the swap to make this week. Pyrex starter set ($40-60).</li>
        <li>Use a second plate as a cover instead of plastic wrap.</li>
      </ul>
      <p>
        See:{' '}
        <Link href="/blog/microwaving-plastic-containers-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microwaving plastic containers
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Day 3: Replace your coffee cup routine
      </h2>
      <p>
        <strong>Why it matters.</strong> Disposable coffee cups have a
        polyethylene or PLA plastic lining. A 2022 study estimated
        ~1,500 microplastic particles per cup released into hot drinks.
        Two coffees a day = ~1.1 million particles per year from one
        habit.
      </p>
      <p>
        <strong>What to do today:</strong>
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Buy a stainless travel mug. Klean Kanteen TKWide ($30), Yeti Rambler ($30), Miir Camp Cup ($28).</li>
        <li>Bring it to the café tomorrow. Most chains and independent cafés will fill it directly, many offer a small discount.</li>
        <li>For-here orders: ask for a ceramic mug instead of a paper cup.</li>
      </ul>
      <p>
        See:{' '}
        <Link href="/blog/microplastics-in-coffee-cups" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in coffee cups
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Day 4: Audit food storage
      </h2>
      <p>
        <strong>Why it matters.</strong> Scratched, warped, faded plastic
        containers shed multiplicatively more than fresh plastic.
        Hot-food contact is the highest-migration condition for
        plasticisers. Glass food storage is dishwasher / microwave /
        freezer safe, lasts decades, and eliminates the question
        entirely.
      </p>
      <p>
        <strong>What to do today:</strong>
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Pull every plastic container out of your kitchen. Toss anything scratched, cloudy, warped, or visibly worn.</li>
        <li>If you don&apos;t have a glass starter set, get one this week (Pyrex Simply Store or Anchor Hocking, $40-60).</li>
        <li>For travel and kids: silicone (Stasher bags) and stainless (LunchBots) are the durable alternatives.</li>
      </ul>
      <p>
        See:{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best plastic-free food storage
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Day 5: Replace plastic cutting boards
      </h2>
      <p>
        <strong>Why it matters.</strong> A 2023 study (Yadav et al.,
        <em>Environmental Science &amp; Technology</em>) estimated that
        polyethylene cutting boards could shed up to{' '}
        <strong>50 grams of microplastic per person per year</strong>{' '}
        directly into the food on the board. Wood and bamboo shed
        nothing.
      </p>
      <p>
        <strong>What to do today:</strong>
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Toss scratched / gouged plastic cutting boards.</li>
        <li>Get a wood or bamboo board for produce and bread: Teakhaus ($50), Greener Chef bamboo ($25), or any local hardwood board.</li>
        <li>Keep a separate glass or stainless prep surface for raw meat if you want dishwasher sanitation.</li>
      </ul>
      <p>
        See:{' '}
        <Link href="/blog/plastic-cutting-board-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          plastic cutting board microplastics
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Day 6: Check tea bags and hot drinks
      </h2>
      <p>
        <strong>Why it matters.</strong> Pyramid mesh tea bags are
        usually nylon or PET. A 2019 McGill study found billions of
        microplastic particles released per cup. Most paper bags also
        use a polypropylene heat-seal.
      </p>
      <p>
        <strong>What to do today:</strong>
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Switch to loose-leaf tea with a stainless or glass infuser ($10-15), cheapest setup, cleanest result.</li>
        <li>If you prefer bags: Numi, Pukka, Clipper, Traditional Medicinals, Yogi, or Tetley (UK) are explicitly plastic-free.</li>
        <li>If you have a plastic electric kettle, consider replacing with a glass kettle (Cosori, $50) or fully stainless (Bonavita, $95).</li>
      </ul>
      <p>
        See:{' '}
        <Link href="/blog/microplastics-in-tea-bags" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in tea bags
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Day 7: Scan your pantry, set a baseline
      </h2>
      <p>
        <strong>Why it matters.</strong> Reduction only matters if you
        can see it. Walk through your kitchen with the MicroPlastics
        app and scan the 20-30 items you use most: water bottles,
        food storage, packaged staples, cooking oil, sauces, supplements,
        cosmetics in the bathroom. The app gives each item a 0–100
        risk score with the cited research.
      </p>
      <p>
        <strong>What to do today:</strong>
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Open the app. Scan your water bottle, coffee mug, food storage, and 10 most-used pantry items.</li>
        <li>Save the worst-scoring 5 to your replace list.</li>
        <li>Set a monthly reminder to scan one new item.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        After day 7: the pace that actually sticks
      </h2>
      <p>
        Seven days covers the kitchen, which is where the largest and cheapest
        wins are. It deliberately does not try to cover everything, and the
        two pathways it leaves out are worth naming so you know what is left.
      </p>
      <p>
        <strong>Indoor air and what you wear</strong> are the chronic,
        low-grade exposures that a week of kitchen swaps does not touch.
        Household dust carries synthetic fibres shed from carpet, upholstery
        and clothing, and it is a genuine intake route rather than a
        footnote. Neither has a one-day fix, which is exactly why they sit
        outside a seven-day plan: a HEPA filter and a slow rotation out of
        synthetic fabrics are month-scale changes, not week-scale ones.
      </p>
      <p>
        If you want to keep going after day 7, the pacing that works looks
        roughly like this &mdash; and note how much slower it is than the first
        week, on purpose:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Month 1</strong> &mdash; finish the storage swap. Glass in, scratched plastic out, nothing plastic in the microwave.</li>
        <li><strong>Month 2</strong> &mdash; cookware. One stainless or cast-iron pan, a wooden board, and bin anything with a scored non-stick surface.</li>
        <li><strong>Month 3</strong> &mdash; hot drinks. Loose leaf, your own mug, a glass or steel kettle.</li>
        <li><strong>Ongoing</strong> &mdash; replace filter cartridges on schedule (every 6&ndash;12 months, and a lapsed cartridge is worse than none), audit personal-care products quarterly as you finish them, and let synthetic clothing phase out as it wears rather than replacing it in one go.</li>
      </ul>
      <p>
        On cost, the honest comparison is not against zero. A household of four
        drinking bottled water spends roughly{' '}
        <strong>$1,500&ndash;3,000 a year</strong> on it. Against that, a filter
        and a set of glass containers pay for themselves inside a year or so
        &mdash; the changes in this challenge are mostly a{' '}
        <strong>saving</strong> dressed up as a purchase.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What this challenge is not
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Not a medical detox.</strong> Your body doesn&apos;t flush microplastics in seven days. The &ldquo;detox&rdquo; is exposure reduction, not biological elimination.</li>
        <li><strong>Not all-or-nothing.</strong> Even doing 3 of the 7 days delivers meaningful daily exposure reduction.</li>
        <li><strong>Not expensive.</strong> Total budget version (water filter + glass storage + travel mug + wood board): around $130. Pays back vs bottled water in 2-3 months.</li>
        <li><strong>Not a guarantee of health outcomes.</strong> Reduction is precautionary, supported by published research on exposure pathways.</li>
      </ul>

      {/* Dual-platform CTA, replaces the dead 7-day email sequence. iOS
          users get the App Store install (the actual product). Non-iOS
          users get the Android waitlist (the proven-converting pattern
          from the homepage hero). Both fire tracked events with the
          7day-challenge source so we can A/B this conversion separately
          from the topic-aware bottom CTA that fires later in the layout. */}
      <div className="not-prose my-8 rounded-2xl border border-[#1E6B47]/30 bg-gradient-to-br from-[#1E6B47]/15 via-white to-white p-6 md:p-7 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1E6B47] mb-2">
          Track the 7 days in the app
        </p>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
          The app turns this checklist into a daily tracker
        </h3>
        <p className="text-gray-700 leading-relaxed mb-5 max-w-2xl">
          Scan each swap as you make it. The app saves your daily exposure
          score so you can see this week&apos;s reduction vs your baseline.
          On iOS now; Android coming soon, get notified when it launches.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <AppStoreLink
            source="blog_7day_challenge_app"
            ctaPosition="mid_article"
            trackingParams={{ post_slug: '7-day-microplastic-detox-challenge', post_topic: 'general', page_type: 'blog' }}
            className="inline-flex items-center justify-center gap-2 bg-black text-white font-semibold px-5 py-3 rounded-control hover:bg-black/90 transition-colors pressable"
          >
            <FaApple className="w-4 h-4" aria-hidden="true" />
            Start the challenge, iOS
          </AppStoreLink>
          <button
            type="button"
            onClick={() => setIsWaitlistOpen(true)}
            className="inline-flex items-center justify-center gap-2 bg-white border border-black/10 text-gray-800 px-5 py-3 rounded-xl hover:border-[#1E6B47] hover:text-[#1E6B47] transition font-semibold"
          >
            Notify me. Android
          </button>
        </div>
      </div>
      <WaitlistModal
        isOpen={isWaitlistOpen}
        onClose={() => setIsWaitlistOpen(false)}
        source="blog_7day_challenge_android"
      />

      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, PVC, polycarbonate, glass, aluminum.</>,
          <>Container condition from your photo, scratches, dents, fade, warp.</>,
          <>Brand and product category, different SKUs in the same brand can score differently.</>,
          <>Use-context flags you log, microwave, heat, reuse, age, contents.</>,
          <>Published research backing each scan&apos;s 0–100 score.</>,
          <>Safer alternatives, usually a glass / aluminum / stainless version of the same product or a cleaner brand.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app to build your first microplastic exposure baseline"
        sub="Day 7 of the challenge is built around the scanner. Open the app, scan 10-20 of your most-used items, and you have a starting point you can actually reduce against."
        buttonLabel="Get the MicroPlastics app"
      />

      <p>
        Related reading:{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          30 kitchen swaps
        </Link>,{' '}
        <Link href="/blog/foods-with-the-most-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          25 highest-risk foods
        </Link>,{' '}
        <Link href="/blog/check-products-for-microplastics-before-buying" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          check before you buy
        </Link>,{' '}
        <Link href="/microplastics-guide" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          full 30-day action plan
        </Link>,{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          how to avoid microplastics
        </Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Can you actually detox microplastics from your body in 7 days?',
            answer:
              'No: and this challenge is not about that. Microplastics already in tissue don\'t clear in a week (or in a year for some types). This is an exposure-reduction challenge: cutting the daily intake from bottled water, hot food contact, coffee cups, and other common sources. Lower intake means less new burden going forward.',
          },
          {
            question: 'What\'s the single highest-impact swap?',
            answer:
              'Day 1, filter your tap water and refill into glass or stainless. Bottled water alone delivers more daily microplastic particles than most foods combined (~240,000 particles per liter, Qian 2024 PNAS). A NSF P473-certified pitcher removes 80-99%. This is the cheapest, fastest, most impactful change.',
          },
          {
            question: 'How much does the full 7-day plan cost?',
            answer:
              'Budget version: water filter pitcher ($70), glass food storage starter ($40), stainless travel mug ($30), wood cutting board ($25) = around $165 total. Premium version with reverse osmosis filter and higher-end picks runs $400-600. Either way, payback vs bottled water habit is fast.',
          },
          {
            question: 'What if I can\'t afford to do all 7 days?',
            answer:
              'Pick Day 1 and Day 2. Filter your water ($70) and stop microwaving plastic (free). Those two changes alone account for the majority of typical household microplastic intake reduction. Add other days as budget allows.',
          },
          {
            question: 'Does the challenge work for renters and apartments?',
            answer:
              'Yes. Pitcher filters and countertop RO systems don\'t need plumbing changes. All the food storage and tool swaps are portable. Day 1\'s water filter can move with you.',
          },
          {
            question: 'How do I get my family / partner / roommates on board?',
            answer:
              'Start with the swaps that don\'t require participation. Days 1, 4, 5. A glass food storage set is just an upgrade nobody complains about; a wood cutting board is just a better cutting board. For the behavioral changes (Day 2, Day 3), focus on the convenience benefits (better-tasting coffee from a real mug, no plastic warping in the microwave) rather than the chemistry lecture.',
          },
          {
            question: 'What if I do 7 days and slip back to old habits?',
            answer:
              'Most of the changes are sticky if you do them once: a water filter stays installed, a glass storage set replaces the plastic, a stainless travel mug becomes the default. Day 2 (no microwaving plastic) is the one that requires ongoing awareness, but once you have the glass dishes, transferring is automatic.',
          },
          {
            question: 'Do I need to scan everything in my kitchen?',
            answer:
              'No. Day 7 is about establishing a baseline with the 10-20 items you use most. The scanner is most useful when you\'re making a purchase decision, comparing two brands or two packaging variants on the shelf. Use it as a shopping tool, not a household audit.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'PNAS',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the Release of Microplastics and Nanoplastics from Plastic Containers and Reusable Food Pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Liu G, Wang J, Wang M, et al.',
            year: 2022,
            title: 'Disposable plastic materials release microplastics and harmful substances in hot water',
            publisher: 'Science of the Total Environment',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0048969721075057',
          },
          {
            authors: 'Yadav H, Khan MRH, Quadir M, et al.',
            year: 2023,
            title: 'Cutting Boards: An Overlooked Source of Microplastics in Human Food?',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c00924',
          },
          {
            authors: 'Hernandez LM, Xu EG, Larsson HCE, et al.',
            year: 2019,
            title: 'Plastic Teabags Release Billions of Microparticles and Nanoparticles into Tea',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b02540',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
